import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

interface Organisation {
  _id: string
  organisationId: string
  type: string
  typeDetail: string
  aimag: string
  phoneNumber: string
  ner: string
  ovog: string
  email: string
  registriinDugaar?: string
  createdAt?: string
  lastLogin?: Date
}

interface AuthContextType {
  organisation: Organisation | null
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  loading: boolean
}

interface RegisterData {
  type: string
  typeDetail: string
  aimag: string
  phoneNumber: string
  ner: string
  ovog: string
  registriinDugaar: string
  email: string
  password: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [organisation, setOrganisation] = useState<Organisation | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/organisations/me`, {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        setOrganisation(data)
      } else {
        setOrganisation(null)
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      setOrganisation(null)
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    const response = await fetch(`${API_URL}/api/organisations/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Login failed')
    }

    const data = await response.json()
    setOrganisation(data)
    navigate('/dashboard')
  }

  const register = async (data: RegisterData) => {
    const response = await fetch(`${API_URL}/api/organisations/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Registration failed')
    }

    const orgData = await response.json()
    setOrganisation(orgData)
    navigate('/dashboard')
  }

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/organisations/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch (error) {
      console.error('Logout request failed:', error)
    }

    setOrganisation(null)
    navigate('/')
  }

  return (
    <AuthContext.Provider value={{ organisation, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
